<template>
  <div class="wrapper flex justify-center items-center" :style="bgStyle">

    <!-- OLD Background -->
    <div class="background-layer"></div>

    <!-- Radial Transition Layer -->
    <div class="transition-layer" :class="{ active: changing }"></div>

    <!-- Your animated elements (unchanged) -->
    <div class="absolute inset-0 z-10 opacity-20 pointer-events-none">

      <div class="absolute top-5 left-10 w-24 h-24 rounded-full bg-white animate-float"></div>
      <div class="absolute top-20 right-5 w-16 h-16 rounded-full bg-gold-300 animate-float-delay-1"></div>
      <div class="absolute top-1/4 left-1/4 w-32 h-32 rounded-full bg-white animate-float-delay-2"></div>
      <div class="absolute top-36 right-1/3 w-20 h-20 rounded-full bg-gold-300 animate-float-delay-3"></div>
      <div class="absolute bottom-1/4 left-1/6 w-28 h-28 rounded-full bg-white animate-float-delay-3"></div>
      <div class="absolute bottom-16 right-1/4 w-36 h-36 rounded-full bg-gold-300 animate-float-delay-2"></div>
      <div class="absolute top-10 left-2/3 w-12 h-12 rounded-full bg-white animate-float-delay-6"></div>
      <div class="absolute top-2/3 right-1/2 w-40 h-40 rounded-full bg-gold-300 animate-float-delay-3"></div>
      <div class="absolute bottom-1/3 left-1/3 w-24 h-24 rounded-full bg-white animate-float-delay-2"></div>
      <div class="absolute bottom-10 right-1/6 w-16 h-16 rounded-full bg-gold-300 animate-float-delay-3"></div>

      <div class="absolute top-1/4 left-20 w-16 h-16 rounded-full border-4 border-white animate-pulse-grow"></div>
      <div class="absolute top-1/3 right-1/3 w-12 h-12 rounded-full border-4 border-gold-300 animate-pulse-grow-delay"></div>
      <div class="absolute bottom-1/2 right-10 w-20 h-20 rounded-full border-4 border-white animate-pulse-grow-delay-2"></div>
      <div class="absolute top-1/2 left-1/5 w-14 h-14 rounded-full border-4 border-gold-300 animate-pulse-grow-delay-2"></div>
      <div class="absolute bottom-1/6 right-1/4 w-18 h-18 rounded-full border-4 border-white animate-pulse-grow-delay-2"></div>
      <div class="absolute bottom-20 left-3/4 w-12 h-12 rounded-full border-4 border-gold-300 animate-pulse-grow-delay-2"></div>
      <div class="absolute bottom-10 left-1/2 w-28 h-28 rounded-full bg-white animate-float-delay-3"></div>
      <div class="absolute top-10 right-1/5 w-20 h-20 rounded-full border-4 border-gold-300 animate-pulse-grow-delay-2"></div>
      <div class="absolute bottom-16 left-1/3 w-24 h-24 rounded-full border-4 border-white animate-pulse-grow-delay-2"></div>

    </div>

    <slot></slot>
  </div>
</template>

<script setup>
import { ref, watch } from "vue"
import { useParticipationStore } from "@/stores/useParticipationStore"

const participationStore = useParticipationStore()

/* Brand Gradients */
const brandGradients = {
  cocacola: "radial-gradient(circle, #ff1a1a, #b30000)",
  fanta: "radial-gradient(circle, #ff9f1c, #ff6f00)",
  schweppes: "radial-gradient(circle,#d8cb3eff , #e2c908ff)",
  sprite: "radial-gradient(circle, #6ede8a, #1c8f43)",
}

/* ACTIVE BACKGROUND (what user sees after transition) */
const activeBackground = ref(
  brandGradients[participationStore.participation?.brand || "cocacola"]
)

/* NEXT BACKGROUND (used during radial wipe) */
const nextBackground = ref(activeBackground.value)

/* Controls animation */
const changing = ref(false)

watch(
  () => participationStore.participation?.brand,
  (newBrand) => {
    if (!newBrand) return

    // 1. Set new gradient to transition layer
    nextBackground.value = brandGradients[newBrand]

    // 2. Trigger animation
    changing.value = true

    // 3. After animation ends, apply the new background
    setTimeout(() => {
      activeBackground.value = nextBackground.value
      changing.value = false
    }, 600)
  }
)
</script>

<style scoped>
/* Wrapper container */
.wrapper {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
}

/* Background before animation */
.background-layer {
  position: absolute;
  inset: 0;
  background: v-bind(activeBackground);
  transition: background 0.3s ease-in-out;
  z-index: 0;
}

/* Expanding circle transition layer */
.transition-layer {
  position: absolute;
  inset: 0;
  background: v-bind(nextBackground);
  mask: radial-gradient(circle at center, transparent 0%, black 0%);
  -webkit-mask: radial-gradient(circle at center, transparent 0%, black 0%);
  transition: mask 0.6s ease-out, -webkit-mask 0.6s ease-out;
  z-index: 1;
  pointer-events: none;
}

.transition-layer.active {
  mask: radial-gradient(circle at center, transparent 100%, black 100%);
  -webkit-mask: radial-gradient(circle at center, transparent 100%, black 100%);
}



/* Your animations stay unchanged */
@keyframes float {
  0%,100% { transform: translateY(0px) translateX(0px) }
  50% { transform: translateY(-30px) translateX(20px) }
}

@keyframes float-delay-1 {
  0%,100% { transform: translateY(0px) translateX(0px) }
  50% { transform: translateY(-40px) translateX(-15px) }
}

@keyframes float-delay-2 {
  0%,100% { transform: translateY(0px) translateX(0px) }
  50% { transform: translateY(-25px) translateX(25px) }
}

@keyframes float-delay-3 {
  0%,100% { transform: translateY(0px) translateX(0px) }
  50% { transform: translateY(-35px) translateX(-20px) }
}

@keyframes pulse-grow {
  0%,100% { transform: scale(1); opacity: 1 }
  50% { transform: scale(1.3); opacity: 0.5 }
}

@keyframes pulse-grow-delay {
  0%,100% { transform: scale(1); opacity: 0.8 }
  50% { transform: scale(1.4); opacity: 0.3 }
}

@keyframes pulse-grow-delay-2 {
  0%,100% { transform: scale(1); opacity: 1 }
  50% { transform: scale(1.2); opacity: 0.4 }
}

/* Attach animations */
.animate-float { animation: float 6s ease-in-out infinite }
.animate-float-delay-1 { animation: float-delay-1 7s ease-in-out infinite }
.animate-float-delay-2 { animation: float-delay-2 8s ease-in-out infinite }
.animate-float-delay-3 { animation: float-delay-3 9s ease-in-out infinite }

.animate-pulse-grow { animation: pulse-grow 3s ease-in-out infinite }
.animate-pulse-grow-delay { animation: pulse-grow-delay 3.5s ease-in-out infinite }
.animate-pulse-grow-delay-2 { animation: pulse-grow-delay-2 4s ease-in-out infinite }
</style>
